/**
 * List pages для Підпорції 1.4 (2026-05-25).
 * 2026-08-03 (Активи-UI етап 2b): «Продаж ОЗ» — модалка створення
 * (покупець-контрагент або зовнішній покупець, ПДВ) + провести/розпровести.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { openPrintView } from '@/shared/utils/printView';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames } from '@/shared/hooks/useOrgNames';
import { useAttachmentFlags } from '@/shared/hooks/useAttachmentFlags';
import { useSignControlMulti } from '@/shared/hooks/useSignControl';
import SearchSelect from '@/components/SearchSelect';
import { contractors as contractorsApi } from '@/api/catalogs';
import { fixedAssets } from '@/api/assets';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import {
  inventory,
  sale,
  type FixedAssetInventory,
  type FixedAssetSale,
} from '@/api/asset_inventory_sale';
import TchTable from '@/shared/components/TchTable';


function errDetail(e: unknown): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : JSON.stringify(d ?? String(e));
}


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

/** Стан інвентаризації НА — людською (дзеркало складської InventoryCountsPage). */
const INV_STATE_LABELS: Record<string, [string, string]> = {
  DRAFT: ['чернетка', '#64748b'],
  IN_PROGRESS: ['в процесі', '#b45309'],
  POSTED: ['проведено', '#15803d'],
  CANCELLED: ['скасовано', '#dc2626'],
};


export function InventoryPage() {
  const navigate = useNavigate();
  const f = useDocListFilters();
  const orgNames = useOrgNames();
  const list = useQuery({
    queryKey: ['fa-inventory', ...f.queryKey],
    queryFn: () => inventory.list({
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo, limit: 500,
    }),
  });
  const attF = useAttachmentFlags('doc.fixed_asset_inventory', list.data?.items ?? []);
  const scF = useSignControlMulti('doc.fixed_asset_inventory', list.data?.items ?? []);
  const cols = useMemo<ColumnDef<FixedAssetInventory>[]>(
    () => [
      {
        key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'organization_id', header: 'Організація', width: 190,
        sortValue: (r) => orgNames.get(r.organization_id) ?? '',
        cell: (r) => orgNames.get(r.organization_id) ?? '',
      },
      {
        key: 'location_kind', header: 'Місце проведення', width: 150,
        sortValue: (r) => r.location_kind ?? '',
        cell: (r) => r.location_kind === 'DEPARTMENT' ? 'підрозділ'
          : r.location_kind === 'EMPLOYEE' ? 'МВО (працівник)'
          : r.location_kind === 'ORGANIZATION' ? 'по організації' : (r.location_kind ?? ''),
      },
      {
        key: 'inventory_period_start', header: 'Період з', width: 120,
        sortValue: (r) => r.inventory_period_start ?? '',
        cell: (r) => r.inventory_period_start ? new Date(r.inventory_period_start).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'inventory_period_end', header: 'Період до', width: 120,
        sortValue: (r) => r.inventory_period_end ?? '',
        cell: (r) => r.inventory_period_end ? new Date(r.inventory_period_end).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'items', header: 'Позицій', width: 110, align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        // 2026-08-31: статусний цикл як у складської — DRAFT → IN_PROGRESS → POSTED.
        key: 'state', header: 'Стан', width: 120,
        sortValue: (r) => r.state ?? '',
        cell: (r) => {
          const [label, color] = INV_STATE_LABELS[r.state ?? ''] ?? [r.state ?? '', '#334155'];
          return <span style={{ color, fontWeight: r.state === 'POSTED' ? 600 : 400 }}>{label}</span>;
        },
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
      ...attF.column,
      ...scF.column,
    ],
    [orgNames, attF.column, scF.column],
  );
  // 2026-08-31 (рішення власника): сторінка за шаблоном — .card + фільтри.
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Iнвентаризація ОЗ</h2>
        <button className="primary" style={{ marginLeft: 'auto' }} onClick={() => navigate('/assets/inventory/new')}>+ Нова інвентаризація</button>
        <span style={{ color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <DataTable<FixedAssetInventory>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        onRowDoubleClick={(r) => navigate(`/assets/inventory/${r.id}`)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}


// ============================ Продаж ОЗ: модалка =====================


interface SaleRow {
  fixed_asset_id: string;
  sale_price_without_vat: string;
  vat_rate_percent: string;
}

function SaleModal({ onClose, initialAssetId, initialOrgId }: {
  onClose: () => void; initialAssetId?: string; initialOrgId?: string;
}) {
  const qc = useQueryClient();
  const { availableOrgs } = usePermissionsByOrg('documents.fixed_asset_sale.create');
  const [orgId, setOrgId] = useState(initialOrgId ?? '');
  const orgEff = orgId || availableOrgs[0]?.id || '';
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [buyerMode, setBuyerMode] = useState<'contractor' | 'external'>('contractor');
  const [contractorId, setContractorId] = useState('');
  const [extName, setExtName] = useState('');
  const [extTaxId, setExtTaxId] = useState('');
  const [comment, setComment] = useState('');
  const [rows, setRows] = useState<SaleRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const faQ = useQuery({
    queryKey: ['fixed-assets-v2', orgEff],
    queryFn: () => fixedAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff,
  });
  const used = new Set(rows.map((r) => r.fixed_asset_id));
  const candidates = (faQ.data?.items ?? []).filter(
    (a) => ['OPERATING', 'PARKED', 'CONSERVATION'].includes(a.status)
      && !used.has(a.id));
  const faById = (id: string) => (faQ.data?.items ?? []).find((a) => a.id === id);
  // Prefill з картки активу (2026-08-27).
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (!initialAssetId || prefilled) return;
    const a = candidates.find((x) => x.id === initialAssetId);
    if (!a) return;
    setRows((rs) => [...rs, { fixed_asset_id: a.id, sale_price_without_vat: a.residual_value, vat_rate_percent: '20' }]);
    setPrefilled(true);
  });

  const vatOf = (r: SaleRow) => {
    const p = Number(r.sale_price_without_vat) || 0;
    const rate = Number(r.vat_rate_percent) || 0;
    return Math.round(p * rate) / 100;
  };
  const totalNoVat = rows.reduce((s, r) => s + (Number(r.sale_price_without_vat) || 0), 0);
  const totalVat = rows.reduce((s, r) => s + vatOf(r), 0);

  const buyerOk = buyerMode === 'contractor' ? !!contractorId : !!extName.trim();
  const rowsOk = rows.length > 0
    && rows.every((r) => Number(r.sale_price_without_vat) > 0);

  const createdId = useRef<string | null>(null);
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      // ч.1343: якщо проведення відмовило, документ уже створено — повторне «провести»
      // оновлює ЙОГО (з виправленнями у вікні), а не створює другий такий самий.
      const body = {
        organization_id: orgEff,
        document_number: null,
        document_date: `${docDate}T12:00:00`,
        counterparty_id: buyerMode === 'contractor' ? contractorId : null,
        external_buyer_name: buyerMode === 'external' ? extName.trim() : null,
        external_buyer_tax_id:
          buyerMode === 'external' && extTaxId.trim() ? extTaxId.trim() : null,
        agreement_text: null,
        comment: comment || null,
        items: rows.map((r, i) => ({
          line_number: i + 1,
          fixed_asset_id: r.fixed_asset_id,
          sale_price_without_vat: r.sale_price_without_vat,
          vat_rate_percent: r.vat_rate_percent || '20',
          vat_amount_override: null,
          item_note: null,
        })),
      };
      const doc = createdId.current
        ? await sale.update(createdId.current, body)
        : await sale.create(body);
      createdId.current = doc.id;
      if (andPost) await sale.post(doc.id);
      return doc;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fa-sale'] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
      onClose();
    },
    onError: (e: unknown) => setError(errDetail(e)),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div className="card" style={{ width: 860, maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Продаж ОЗ</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 9' }}>
            <label>Організація</label>
            <select value={orgEff}
              onChange={(e) => { setOrgId(e.target.value); setRows([]); }}>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Дата</label>
            <input type="date" value={docDate}
              onChange={(e) => setDocDate(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>
            <input type="radio" checked={buyerMode === 'contractor'}
              onChange={() => setBuyerMode('contractor')} />{' '}
            Покупець-контрагент
            <span style={{ marginLeft: '1rem' }}>
              <input type="radio" checked={buyerMode === 'external'}
                onChange={() => setBuyerMode('external')} />{' '}
              Зовнішній покупець (фізособа)
            </span>
          </label>
        </div>
        {buyerMode === 'contractor' ? (
          <div className="field">
            <label>Контрагент *</label>
            <SearchSelect
              items={(contractorsQ.data?.items ?? []).map((c) => ({
                id: c.id, label: c.name, hint: c.edrpou ?? null,
              }))}
              value={contractorId}
              onChange={setContractorId}
            />
          </div>
        ) : (
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 8' }}>
              <label>ПІБ / назва покупця *</label>
              <input value={extName} onChange={(e) => setExtName(e.target.value)} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>РНОКПП (необовʼязково)</label>
              <input value={extTaxId} onChange={(e) => setExtTaxId(e.target.value)} />
            </div>
          </div>
        )}
        <h4 style={{ margin: '0.5rem 0 0.25rem' }}>Активи ({rows.length})</h4>
        <TchTable>
          {rows.length > 0 && (
            <thead>
              <tr style={{ color: 'var(--muted)' }}>
                <th style={{ textAlign: 'left' }}>Актив</th>
                <th style={{ width: 120 }}>Залишкова</th>
                <th style={{ width: 130 }}>Ціна без ПДВ *</th>
                <th style={{ width: 90 }}>ПДВ %</th>
                <th style={{ width: 110, textAlign: 'right' }}>ПДВ</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((r, i) => {
              const a = faById(r.fixed_asset_id);
              return (
                <tr key={r.fixed_asset_id}>
                  <td>
                    {a ? `${a.inventory_number} ${a.name}` : '—'}
                  </td>
                  <td style={{ textAlign: 'right', color: 'var(--muted)' }}>
                    {a ? fmt2(a.residual_value) : ''}
                  </td>
                  <td>
                    <input style={{ textAlign: 'right' }}
                      value={r.sale_price_without_vat}
                      onChange={(e) => setRows((rs) => rs.map((x, idx) =>
                        idx === i ? { ...x, sale_price_without_vat: e.target.value } : x))} />
                  </td>
                  <td>
                    <select value={r.vat_rate_percent}
                      onChange={(e) => setRows((rs) => rs.map((x, idx) =>
                        idx === i ? { ...x, vat_rate_percent: e.target.value } : x))}>
                      <option value="20">20</option>
                      <option value="14">14</option>
                      <option value="7">7</option>
                      <option value="0">0</option>
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmt2(vatOf(r))}</td>
                  <td>
                    <button className="danger"
                      onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
        <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Додати ОЗ</h4>
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border-soft)', borderRadius: 6 }}>
          <table className="tch">
            <tbody>
              {candidates.length === 0 && (
                <tr><td style={{ color: 'var(--muted)' }}>
                  Немає доступних ОЗ.
                </td></tr>
              )}
              {candidates.map((a) => (
                <tr key={a.id} style={{ cursor: 'pointer' }}
                    onClick={() => setRows((rs) => [...rs, {
                      fixed_asset_id: a.id,
                      sale_price_without_vat: a.residual_value,
                      vat_rate_percent: '20',
                    }])}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                  <td>{a.inventory_number} {a.name}</td>
                  <td style={{ textAlign: 'right', width: 150, color: 'var(--muted)' }}>
                    залишкова {fmt2(a.residual_value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ textAlign: 'right', fontWeight: 600, margin: '0.5rem 0' }}>
          Разом без ПДВ: {fmt2(totalNoVat)} · ПДВ: {fmt2(totalVat)} · З ПДВ:{' '}
          {fmt2(totalNoVat + totalVat)}
        </div>
        <div className="field">
          <label>Коментар</label>
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        {error && <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="primary"
            disabled={!buyerOk || !rowsOk || save.isPending}
            onClick={() => save.mutate(true)}>
            {save.isPending ? 'виконується…' : 'провести'}
          </button>
          <button disabled={!buyerOk || !rowsOk || save.isPending}
            onClick={() => save.mutate(false)}>
            зберегти чернетку
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}


export function SalePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(!!sp.get('asset_id'));
  const list = useQuery({
    queryKey: ['fa-sale'],
    queryFn: () => sale.list({ limit: 200 }),
  });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const postMut = useMutation({
    mutationFn: ({ id, posted }: { id: string; posted: boolean }) =>
      posted ? sale.unpost(id) : sale.post(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fa-sale'] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
    },
    onError: (e: unknown) => alert(errDetail(e)),
  });
  const buyerLabel = (r: FixedAssetSale) =>
    r.counterparty_id
      ? ((contractorsQ.data?.items ?? []).find((c) => c.id === r.counterparty_id)?.name
         ?? `Контрагент ${r.counterparty_id.slice(0, 8)}…`)
      : r.external_buyer_name ?? '—';
  const saleTotal = (r: FixedAssetSale) =>
    (r.items ?? [])
      .map((i) => Number(i.sale_price_with_vat ?? 0))
      .reduce((a, b) => a + b, 0);
  const cols = useMemo<ColumnDef<FixedAssetSale>[]>(
    () => [
      {
        key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'buyer', header: 'Покупець',
        sortValue: (r) => buyerLabel(r),
        cell: (r) => buyerLabel(r),
      },
      {
        key: 'items', header: 'Позицій', width: 110, align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'total_with_vat', header: 'Сума з ПДВ', width: 150, align: 'right',
        sortValue: (r) => saleTotal(r),
        cell: (r) => fmt2(saleTotal(r)),
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
      {
        key: 'actions', header: '', width: 200, align: 'center',
        cell: (r) => (
          <span style={{ display: 'inline-flex', gap: '0.35rem' }}>
            <button style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem' }}
              disabled={postMut.isPending}
              onClick={(e) => { e.stopPropagation(); postMut.mutate({ id: r.id, posted: r.posted }); }}>
              {r.posted ? 'розпровести' : 'провести'}
            </button>
            {r.posted && (
              <button style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem' }}
                onClick={(e) => {
                  e.stopPropagation();
                  void openPrintView(sale.printViewUrl(r.id));
                }}>
                друк
              </button>
            )}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postMut, contractorsQ.data],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Продаж ОЗ</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/assets/sale/new')}>
          + Новий документ
        </button>
        <span style={{ color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<FixedAssetSale>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        onRowDoubleClick={(r) => navigate(`/assets/sale/${r.id}`)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
      {createOpen && <SaleModal onClose={() => setCreateOpen(false)}
        initialAssetId={sp.get('asset_id') ?? undefined} initialOrgId={sp.get('organization_id') ?? undefined} />}
    </div>
  );
}
